import { useEffect, useState } from 'react';
import { Space, DatePicker, InputNumber, Modal, Popconfirm, Tooltip, Typography, message } from 'antd';

import { formatDate, formatToDollar, isSupportManager } from '../../../../helpers/genericHelper';
import API from '../../../../services/API';

const { Text, Link } = Typography;

// One item in the account's status strip: a colored dot, a label and a value, with details on hover
export const StatusItem = ({ label, ok, value, details, action }) => (
  <div className="furnish-status-item">
    <span className={`furnish-dot ${ok ? 'ok' : 'bad'}`} />
    <span>{label}:</span>
    <Tooltip title={details}>
      <Text strong type={ok ? undefined : 'danger'}>{value}</Text>
    </Tooltip>
    {action}
  </div>
);

const eventDetails = (event, extra = []) => [
  event?.effective_date && `Effective ${formatDate(event.effective_date)}`,
  event?.created_at && `Added ${formatDate(event.created_at)}`,
  ...extra
].filter(Boolean).join(' · ') || null;

// Closure, charge off and bankruptcy as reported to Bloom, for the account's status strip
export default function BloomAccountStatus({ productId, reloadData, isCC = true }) {
  // Save & Build calls a charge off a write off
  const chargeOffName = isCC ? 'Charge off' : 'Write off';

  const [bloomAccount, setBloomAccount] = useState(null);
  const [closureEffectiveDate, setClosureEffectiveDate] = useState(null);
  const [showClosureForm, setShowClosureForm] = useState(false);
  const [chargeOffEffectiveDate, setChargeOffEffectiveDate] = useState(null);
  const [chargeOffAmount, setChargeOffAmount] = useState(null);
  const [showChargeOffForm, setShowChargeOffForm] = useState(false);

  const resetClosureForm = () => {
    setShowClosureForm(false);
    setClosureEffectiveDate(null);
  };

  const resetChargeOffForm = () => {
    setShowChargeOffForm(false);
    setChargeOffEffectiveDate(null);
    setChargeOffAmount(null);
  };

  useEffect(() => {
    getBloomAccount();
  }, [productId]);

  const getBloomAccount = async () => {
    const bloomAccountData = await API.getFurnishingBloomAccount({ productId });
    setBloomAccount(bloomAccountData);
  }

  const postBloomAccountEvent = async (event, successMessage) => {
    try {
      const response = await API.postFurnishingBloomAccountEvent({
        accountId: bloomAccount.id.value,
        ...event
      });
      // Any 2xx reply means the event was saved (errors throw and land in catch);
      // only an explicit success: false from the backend is treated as a failure.
      if (response?.success === false) {
        message.error(response?.message || 'Failed to update bloom account.');
      } else {
        message.success(successMessage);
        getBloomAccount();
      }
      reloadData?.();
    } catch (error) {
      message.error('Failed to update bloom account.');
      console.error(error);
    }
  }

  const addClosure = async () => {
    await postBloomAccountEvent({
      eventType: 'CLOSURE',
      effectiveDate: closureEffectiveDate ? closureEffectiveDate.format('YYYY-MM-DD') : null
    }, 'Closure added.');
    resetClosureForm();
  };

  const reverseClosure = () => postBloomAccountEvent({ eventType: 'CLOSURE', reversed: true, effectiveDate: bloomAccount?.accountPosition?.effective_date }, 'Closure reversed.');

  const addChargeOff = async () => {
    await postBloomAccountEvent({
      eventType: 'CHARGE_OFF',
      effectiveDate: chargeOffEffectiveDate ? chargeOffEffectiveDate.format('YYYY-MM-DD') : null,
      amount: chargeOffAmount != null ? String(chargeOffAmount) : null
    }, `${chargeOffName} added.`);
    resetChargeOffForm();
  };

  const reverseChargeOff = () => postBloomAccountEvent({
    eventType: 'CHARGE_OFF',
    reversed: true,
    effectiveDate: bloomAccount?.chargeOff?.effective_date,
    amount: bloomAccount?.chargeOff?.amount
  }, `${chargeOffName} reversed.`);

  const closure = bloomAccount?.accountPosition;
  const chargeOff = bloomAccount?.chargeOff;
  const bankruptcy = bloomAccount?.bankruptcy;

  const managerLink = (label, onClick, confirm) => isSupportManager() && (confirm
    ? <Popconfirm title={confirm} onConfirm={onClick}><Link className="furnish-status-action">{label}</Link></Popconfirm>
    : <Link className="furnish-status-action" onClick={onClick}>{label}</Link>);

  return (
    <>
      <StatusItem
        label="Closure"
        ok={!closure}
        value={closure ? `Closed ${formatDate(closure.effective_date) || ''}`.trim() : 'None'}
        details={eventDetails(closure)}
        action={closure
          ? managerLink('Reverse', reverseClosure, 'Reverse this closure?')
          : managerLink('Add', () => setShowClosureForm(true))} />
      <StatusItem
        label={chargeOffName}
        ok={!chargeOff}
        // Bloom sends the amount as text, which formatToDollar leaves unformatted
        value={chargeOff ? (chargeOff.amount != null ? formatToDollar(Number(chargeOff.amount)) : 'Reported') : 'None'}
        details={eventDetails(chargeOff)}
        action={chargeOff
          ? managerLink('Reverse', reverseChargeOff, `Reverse this ${chargeOffName.toLowerCase()}?`)
          : managerLink('Add', () => setShowChargeOffForm(true))} />
      <StatusItem
        label="Bankruptcy"
        ok={!bankruptcy}
        value={bankruptcy ? `Chapter ${bankruptcy.chapter || '?'}${bankruptcy.status ? ` · ${bankruptcy.status}` : ''}` : 'None'}
        details={eventDetails(bankruptcy)} />

      <Modal
        title="Add Closure"
        open={showClosureForm}
        onOk={addClosure}
        onCancel={resetClosureForm}
        okText="Submit"
        destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">Reports this account to Bloom as closed from the effective date.</Text>
          <DatePicker
            placeholder="Effective date"
            value={closureEffectiveDate}
            onChange={(date) => setClosureEffectiveDate(date)}
            style={{ width: '100%' }}
          />
        </Space>
      </Modal>

      <Modal
        title={isCC ? 'Add Charge Off' : 'Add Write Off'}
        open={showChargeOffForm}
        onOk={addChargeOff}
        onCancel={resetChargeOffForm}
        okText="Submit"
        okButtonProps={{ danger: true }}
        destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">Reports this account to Bloom as {isCC ? 'charged off' : 'written off'}. This hurts the user's credit report.</Text>
          <DatePicker
            placeholder="Effective date"
            value={chargeOffEffectiveDate}
            onChange={(date) => setChargeOffEffectiveDate(date)}
            style={{ width: '100%' }}
          />
          <InputNumber
            placeholder="Amount"
            prefix="$"
            value={chargeOffAmount}
            onChange={(value) => setChargeOffAmount(value)}
            style={{ width: '100%' }}
          />
        </Space>
      </Modal>
    </>
  )
}
